/**
 * Pure helpers for the Admin Settings pages (docs/plans/ADMIN_SETTINGS_SCOPE_2026_09_29.md).
 * Kept free of React so the rules are unit-tested directly.
 */

export type SettingType = 'sender_email' | 'text' | 'timezone' | 'int' | 'enum';

export interface SettingChoice {
  value: string;
  label: string;
}

export interface AdminSetting {
  key: string;
  category: string;
  label: string;
  description: string;
  type: SettingType;
  value: string | number | null;
  default: string | number | null;
  default_display: string;
  is_overridden: boolean;
  choices: SettingChoice[];
  min_value: number | null;
  max_value: number | null;
  updated_at: string | null;
  updated_by_email: string | null;
}

export interface SettingsResponse {
  settings: AdminSetting[];
  categories: string[];
  info: Record<string, string | null>;
}

/** What the row shows as the current value. */
export function displayValue(setting: AdminSetting): string {
  if (!setting.is_overridden || setting.value === null || setting.value === undefined) {
    return setting.default_display || 'Not set';
  }
  if (setting.type === 'enum') {
    const choice = setting.choices.find(c => c.value === setting.value);
    return choice ? choice.label : String(setting.value);
  }
  return String(setting.value);
}

/** The value to pre-fill the editor with. */
export function initialDraft(setting: AdminSetting): string {
  if (setting.is_overridden && setting.value !== null && setting.value !== undefined) {
    return String(setting.value);
  }
  if (setting.default !== null && setting.default !== undefined) return String(setting.default);
  return '';
}

/**
 * Turn the editor's text into the JSON value the API expects, or return an
 * error message. The server validates again; this only catches the obvious.
 */
export function coerceDraft(setting: AdminSetting, draft: string): { value?: string | number; error?: string } {
  const trimmed = draft.trim();
  if (!trimmed) return { error: 'Enter a value, or use Reset to go back to the default.' };
  if (setting.type === 'int') {
    if (!/^-?\d+$/.test(trimmed)) return { error: 'Enter a whole number.' };
    const n = parseInt(trimmed, 10);
    if (setting.min_value !== null && n < setting.min_value) return { error: `Must be at least ${setting.min_value}.` };
    if (setting.max_value !== null && n > setting.max_value) return { error: `Must be at most ${setting.max_value}.` };
    return { value: n };
  }
  if (setting.type === 'enum') {
    if (!setting.choices.some(c => c.value === trimmed)) return { error: 'Choose one of the options.' };
    return { value: trimmed };
  }
  if (setting.type === 'sender_email' && !/^[^\s@]+@coachcedar\.com$/i.test(trimmed)) {
    return { error: 'Sender addresses must be on @coachcedar.com.' };
  }
  return { value: trimmed };
}

/** The server's explanation from an AdminApiError body, else the error text. */
export function errorMessage(err: unknown): string {
  const body = (err as { body?: string } | null)?.body;
  if (body) {
    try {
      const parsed = JSON.parse(body);
      if (parsed && typeof parsed.detail === 'string') return parsed.detail;
    } catch {
      /* not JSON — fall through */
    }
  }
  if (err instanceof Error && err.message) return err.message;
  return 'Something went wrong.';
}

/** "Changed by paul@… on 29 Sep 2026" or '' when never changed. */
export function changedLine(setting: AdminSetting): string {
  if (!setting.is_overridden || !setting.updated_at) return '';
  const when = new Date(setting.updated_at).toLocaleString();
  return setting.updated_by_email ? `Changed by ${setting.updated_by_email} · ${when}` : `Changed ${when}`;
}

type StoredPermissions = { capabilities?: unknown } | unknown[] | string | null | undefined;

export interface AdminMe {
  admin_id: string;
  email: string;
  full_name: string | null;
  role: string;
  permissions: StoredPermissions;
}

/**
 * Mirrors services/admin_capabilities.capabilities_of: the stored list may be
 * `{capabilities: [...]}`, a bare list, `{}`, or any of those as a JSON string
 * (asyncpg returns JSONB as text and /auth/me passes it through).
 */
export function capabilitiesOf(permissions: StoredPermissions): string[] {
  let perms: unknown = permissions;
  if (typeof perms === 'string') {
    try {
      perms = JSON.parse(perms);
    } catch {
      return [];
    }
  }
  if (perms && !Array.isArray(perms) && typeof perms === 'object') {
    perms = (perms as { capabilities?: unknown }).capabilities;
  }
  return Array.isArray(perms) ? perms.filter((c): c is string => typeof c === 'string') : [];
}

/**
 * Mirrors services/admin_capabilities.has_capability: super_admin holds every
 * capability; everyone else needs it in their stored list. The server enforces
 * this regardless — the UI only hides controls that would 403.
 */
export function adminCan(me: AdminMe | null | undefined, capability: string): boolean {
  if (!me) return false;
  if (me.role === 'super_admin') return true;
  return capabilitiesOf(me.permissions).includes(capability);
}
